import { Plus } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Tourney } from 'modules/tournaments/domain/interfaces/tourney';
import { clearSelectedChampionship } from 'modules/tournaments/store/championship/actions';
import UDButton from 'ud-ui/components/button';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { theme } from 'styles/theme/theme';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { TournamentsBody } from 'modules/tournaments/ui/components/tourneys-body/tournaments-body';
import { TourneysBodySkeleton } from 'modules/tournaments/ui/components/tourneys-body/tourneys-body-skeleton';

import TournamentsFilters from '../components/tourneys-filters';
import { useTourneysList } from '../hooks/use-tourneys-list';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';

const TournamentsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setHeaderContent } = useMainLayout();
  const { info, isLoading: isLoadingOrganizer } = useOrganizer();

  const { tourneys, years, filters, changeFilter, isLoading, pages } = useTourneysList();

  const goToTournamentCreate = useCallback(() => {
    dispatch(clearSelectedChampionship());
    navigate(Pages.TOURNEYS.CREATE);
  }, [dispatch, navigate]);

  const goToEdit = useCallback(
    (id: Tourney['id']) => {
      navigate(Pages.TOURNEYS.builders.edit(id));
    },
    [navigate],
  );

  const bodyNode = useMemo(() => {
    if (isLoading || isLoadingOrganizer) {
      return <TourneysBodySkeleton resultsOnPage={filters.perPage} />;
    }

    const tourneysWithCity = tourneys.map((tourney) => {
      const tourneyAddress = tourney?.locations?.[0] && {
        city: tourney?.locations?.[0]?.address?.city,
        country: tourney?.locations?.[0]?.address?.country,
      };

      return { ...tourney, address: tourneyAddress ?? info?.address };
    });

    return (
      <TournamentsBody
        filters={filters}
        tourneys={tourneysWithCity}
        pages={pages}
        onDetail={goToEdit}
        onPageChange={(page) => changeFilter({ page })}
      />
    );
  }, [isLoading, isLoadingOrganizer, tourneys, filters, pages, goToEdit, info?.address, changeFilter]);

  useEffect(() => {
    setHeaderContent(
      <>
        <UDHeaderTitle>Турниры</UDHeaderTitle>
        <UDButton variant="primary" className="d-flex align-items-center ml-6" onClick={goToTournamentCreate}>
          <Plus size={16} className="mr-2" color={theme.colors.DarkBlue} />
          {t('championships.createTournamentButtonTitle')}
        </UDButton>
      </>,
    );

    return () => {
      setHeaderContent(null);
    };
  }, [goToTournamentCreate, setHeaderContent, t]);

  return (
    <>
      <TournamentsFilters onFilter={changeFilter} filters={filters} years={years} />
      {bodyNode}
    </>
  );
};

export default TournamentsListPage;
